// frontend/src/stores/publicDosingStore.ts
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type {
  PublicDosingBatch,
  PublicDosingContainer,
  PublicDosingElement,
} from '@/types/publicDosing';
import { syncBatchToUrl, getBatchFromUrl } from '@/utils/dosingUrlSync';

const DEFAULT_BATCH: PublicDosingBatch = {
  name: 'Monthly Dosing',
  days: 30,
  containers: [],
};

interface PublicDosingState {
  batch: PublicDosingBatch;

  // FIX A4: Internal flags to prevent race conditions
  _initializedFromUrl: boolean;
  _userInteracted: boolean;

  // Batch metadata
  setBatchName: (name: string) => void;
  setBatchDays: (days: string | number) => void;

  // Container management
  addContainer: () => void;
  updateContainer: (index: number, updates: Partial<PublicDosingContainer>) => void;
  removeContainer: (index: number) => void;

  // Element management
  addElement: (containerIndex: number, elementName: string) => void;
  updateElement: (
    containerIndex: number,
    elementIndex: number,
    updates: Partial<PublicDosingElement>
  ) => void;
  removeElement: (containerIndex: number, elementIndex: number) => void;

  // Calculations
  getContainerTotal: (containerIndex: number) => number;
  getElementTotal: (containerIndex: number, elementIndex: number) => number;
  getRodiWater: (containerIndex: number) => number;
  getBatchTotal: () => number;

  // State management
  loadBatch: (batch: PublicDosingBatch) => void;
  reset: () => void;
  initFromUrl: () => void;
}

// FIX C4: Helper to clamp numeric values
const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

export const usePublicDosingStore = create<PublicDosingState>()(
  subscribeWithSelector((set, get) => ({
    batch: { ...DEFAULT_BATCH },
    _initializedFromUrl: false,
    _userInteracted: false,

    setBatchName: (name) => {
      set((state) => ({
        _userInteracted: true, // FIX A4: Mark user interaction
        batch: { ...state.batch, name },
      }));
    },

    setBatchDays: (days) => {
      // FIX C4: Validate and clamp days
      // Handle both string and number input from NumberInput
      const numericDays = typeof days === 'string' ? parseFloat(days) : days;
      const validDays = Number.isNaN(numericDays) ? 1 : clamp(Math.round(numericDays), 1, 365);
      set((state) => ({
        _userInteracted: true,
        batch: { ...state.batch, days: validDays },
      }));
    },

    addContainer: () => {
      set((state) => {
        const containerNumber = state.batch.containers.length + 1;
        const newContainer: PublicDosingContainer = {
          name: `Container ${containerNumber}`,
          ml_per_day: 10,
          elements: [],
        };
        return {
          _userInteracted: true,
          batch: {
            ...state.batch,
            containers: [...state.batch.containers, newContainer],
          },
        };
      });
    },

    updateContainer: (index, updates) => {
      set((state) => {
        const containers = [...state.batch.containers];
        // FIX C4: Guard against invalid index
        if (index < 0 || index >= containers.length) return state;

        // FIX C4: Validate ml_per_day if provided
        const validUpdates = { ...updates };
        if (typeof validUpdates.ml_per_day === 'number') {
          validUpdates.ml_per_day = clamp(validUpdates.ml_per_day, 0, 100000);
        }

        containers[index] = { ...containers[index], ...validUpdates } as PublicDosingContainer;
        return {
          _userInteracted: true,
          batch: { ...state.batch, containers },
        };
      });
    },

    removeContainer: (index) => {
      set((state) => {
        // FIX C4: Guard against invalid index
        if (index < 0 || index >= state.batch.containers.length) return state;
        return {
          _userInteracted: true,
          batch: {
            ...state.batch,
            containers: state.batch.containers.filter((_, i) => i !== index),
          },
        };
      });
    },

    addElement: (containerIndex, elementName) => {
      set((state) => {
        const containers = [...state.batch.containers];
        // FIX C4: Guard against invalid container index
        if (containerIndex < 0 || containerIndex >= containers.length) return state;

        const container = containers[containerIndex];
        if (!container) return state;
        const updatedContainer = {
          ...container,
          elements: [
            ...(container.elements || []),
            { element_name: elementName, ml_per_day: 0 },
          ],
        } as PublicDosingContainer;
        containers[containerIndex] = updatedContainer;
        return {
          _userInteracted: true,
          batch: { ...state.batch, containers },
        };
      });
    },

    updateElement: (containerIndex, elementIndex, updates) => {
      set((state) => {
        const containers = [...state.batch.containers];
        // FIX C4: Guard against invalid indices
        if (containerIndex < 0 || containerIndex >= containers.length) return state;

        const container = containers[containerIndex];
        if (!container) return state;
        const elements = container.elements || [];
        if (elementIndex < 0 || elementIndex >= elements.length) return state;

        const newElements = [...elements];

        // FIX C4: Validate ml_per_day if provided
        const validUpdates = { ...updates };
        if (typeof validUpdates.ml_per_day === 'number') {
          validUpdates.ml_per_day = clamp(validUpdates.ml_per_day, 0, 10000);
        }

        newElements[elementIndex] = { ...newElements[elementIndex], ...validUpdates } as PublicDosingElement;
        const updatedContainer = {
          ...container,
          elements: newElements,
        } as PublicDosingContainer;
        containers[containerIndex] = updatedContainer;
        return {
          _userInteracted: true,
          batch: { ...state.batch, containers },
        };
      });
    },

    removeElement: (containerIndex, elementIndex) => {
      set((state) => {
        const containers = [...state.batch.containers];
        // FIX C4: Guard against invalid indices
        if (containerIndex < 0 || containerIndex >= containers.length) return state;

        const container = containers[containerIndex];
        if (!container) return state;
        const elements = container.elements || [];
        if (elementIndex < 0 || elementIndex >= elements.length) return state;

        const updatedContainer = {
          ...container,
          elements: elements.filter((_, i) => i !== elementIndex),
        } as PublicDosingContainer;
        containers[containerIndex] = updatedContainer;
        return {
          _userInteracted: true,
          batch: { ...state.batch, containers },
        };
      });
    },

    getContainerTotal: (containerIndex) => {
      const { batch } = get();
      const container = batch.containers[containerIndex];
      if (!container) return 0;
      return container.ml_per_day * batch.days;
    },

    getElementTotal: (containerIndex, elementIndex) => {
      const { batch } = get();
      const container = batch.containers[containerIndex];
      if (!container) return 0;
      const element = container.elements[elementIndex];
      if (!element) return 0;
      return element.ml_per_day * batch.days;
    },

    getRodiWater: (containerIndex) => {
      const { batch, getContainerTotal } = get();
      const container = batch.containers[containerIndex];
      if (!container) return 0;
      const containerTotal = getContainerTotal(containerIndex);
      const elementsTotal = container.elements.reduce(
        (sum, el) => sum + el.ml_per_day * batch.days,
        0
      );
      // FIX C5: Clamp to 0 minimum - can't have negative water
      return Math.max(0, containerTotal - elementsTotal);
    },

    getBatchTotal: () => {
      const { batch } = get();
      return batch.containers.reduce(
        (sum, c) => sum + c.ml_per_day * batch.days,
        0
      );
    },

    loadBatch: (batch) => {
      set({ batch });
    },

    reset: () => {
      // FIX A4: Reset flags when resetting batch
      syncBatchToUrl.cancel(); // Cancel pending syncs
      set({
        batch: { ...DEFAULT_BATCH, containers: [] },
        _initializedFromUrl: false,
        _userInteracted: false,
      });
    },

    initFromUrl: () => {
      const { _initializedFromUrl, _userInteracted } = get();

      // FIX A4: Only load from URL once, and only if user hasn't interacted
      if (_initializedFromUrl || _userInteracted) {
        return;
      }

      const urlBatch = getBatchFromUrl();
      if (urlBatch) {
        set({
          batch: urlBatch,
          _initializedFromUrl: true,
        });
      } else {
        set({ _initializedFromUrl: true });
      }
    },
  }))
);

// Subscribe to state changes and sync to URL
// FIX A4: Only sync after initialization is complete
if (typeof window !== 'undefined') {
  usePublicDosingStore.subscribe(
    (state) => ({ batch: state.batch, initialized: state._initializedFromUrl }),
    ({ batch, initialized }) => {
      // Don't sync until we've finished initializing from URL
      if (initialized) {
        syncBatchToUrl(batch);
      }
    },
    { equalityFn: (a, b) => a.batch === b.batch && a.initialized === b.initialized }
  );
}
